<!-- eslint-disable vuejs-accessibility/click-events-have-key-events, vuejs-accessibility/mouse-events-have-key-events, vuejs-accessibility/no-static-element-interactions -->
<!-- eslint-disable vue/no-v-html -->
<template>
  <div
    :id="resultId"
    :class="[
      'search-result-marker',
      { selected: isSelected, hovered: markerHovered },
    ]"
    @click.stop="handleMarkerClick"
    @mouseenter="handleMarkerMouseEnter"
    @mouseleave="handleMarkerMouseLeave"
  >
    <span class="marker-pin" v-html="pinCharacter" />
    <span class="marker-icon" v-html="iconCharacter" />
  </div>
</template>

<script setup lang="ts">
import {
  getGlyphIconFromSearchResultType,
  getBlankIcon,
} from '@/lib/glyph-markers'
import pubsub from '@/lib/pubsub'
import { GLYPH_FONTS, GLYPH_ICON_TYPES } from '@/lib/glyph-markers-constants'
import { ref, computed, onMounted } from 'vue'

interface SearchResultMarkerProps {
  resultId: string
  resultType: string
  markerStyle?: string
  isSelected?: boolean
}
const {
  resultId,
  resultType,
  markerStyle = GLYPH_FONTS.CIRCLE,
  isSelected = false,
} = defineProps<SearchResultMarkerProps>()

const markerHovered = ref<boolean>(false)

const iconCharacter = getGlyphIconFromSearchResultType(resultType)
const iconFontFamily = ref(GLYPH_FONTS.ICON)

const pinCharacter = computed(() => {
  return isSelected
    ? getBlankIcon(GLYPH_ICON_TYPES.SELECTED_CIRCLE)
    : getBlankIcon(GLYPH_ICON_TYPES.CIRCLE)
})
const pinFontFamily = computed(() => {
  return isSelected ? GLYPH_FONTS.SELECTED_CIRCLE : markerStyle
})

onMounted(() => {
  pubsub.subscribe('highlight-search-map-marker', (id: string) => {
    if (id === resultId) {
      setMarkerHovered(true)
    }
  })
  pubsub.subscribe('unhighlight-search-map-marker', (id: string) => {
    if (id === resultId) {
      setMarkerHovered(false)
    }
  })
})

const handleMarkerClick = () => {
  if (!isSelected) {
    pubsub.publish('search-map-marker-selected', resultId)
  }
}

const handleMarkerMouseEnter = () => {
  pubsub.publish('highlight-search-map-marker', resultId)
}

const handleMarkerMouseLeave = () => {
  pubsub.publish('unhighlight-search-map-marker', resultId)
}

const setMarkerHovered = (hovered: boolean) => {
  markerHovered.value = hovered
}
</script>

<style scoped lang="scss">
@use '@/styles/system/_animation.scss';

.search-result-marker {
  will-change: transform;
  opacity: 1;
  transition: opacity animation.$timing_250 animation.$ease_standard;
  position: relative;
  cursor: pointer;
  line-height: 1;
  z-index: 1;
  /**
   * Center the marker over the map coordinate. setHTMLElementToMapLocation positions
   * the element by its top-left corner (unlike MapboxGL Marker, which handled
   * centering internally), so this translate(-50%, -50%) is required for correct
   * marker alignment and must be kept in sync with the positioning utility.
   */
  transform: translate(-50%, -50%);

  .marker-pin {
    font-size: 24px;
    font-family: v-bind(pinFontFamily);
    color: var(--ys-color-brand-primary);
    -webkit-text-stroke: 1px var(--ys-color-button-text-primary-rest);
  }

  .marker-icon {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate3d(-50%, -50%, 0);
    font-size: 14px;
    font-family: v-bind(iconFontFamily);
    color: var(--ys-color-button-text-primary-rest);
  }

  &:hover,
  &.hovered {
    .marker-pin {
      font-size: 30px;
    }

    .marker-icon {
      font-size: 18px;
    }
  }

  &.selected {
    z-index: 2;
    cursor: initial;

    .marker-pin {
      font-size: 29px;
    }

    .marker-icon {
      font-size: 13px;
      transform: translate3d(-50%, -80%, 0);
    }
    &:hover,
    &.hovered {
      transform: translate(-50%, -50%);
    }
  }
}
</style>
